// Echarts handbook:
import { useMemo } from 'react'
import { useTheme } from '@mui/material'
import type { IAcChartData } from '@typings'
import { BarChart } from 'echarts/charts'
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components'
import * as echarts from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import ReactEChartsCore from 'echarts-for-react/lib/core'

import { AcBox } from '@components'
import { useChartStyling } from '@hooks'

// Register the required components
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer,
  LegendComponent,
])

// The usage of ReactEChartsCore are same with above.
export const AcChartBarDouble = (chartData: IAcChartData) => {
  const theme = useTheme()
  const {
    barStyles,
    legendStyles,
    topLabelStyles,
    tooltipStyles,
    XaxisLabelStyles,
    YaxisLabelStyles,
  } = useChartStyling()

  const getColor = (i: number) => {
    if (i === 0) return theme.palette.green.main
    return theme.palette.pink.main
  }

  const renderDatasets = useMemo(() => {
    if (!chartData?.datasets?.length) return []
    return chartData.datasets.map((set, i) => ({
      type: 'bar',
      name: set.datasetName,
      data: set.data.map(v => v),
      barCategoryGap: theme.spacing(4),
      color: set.datasetColor || getColor(i),
      label: topLabelStyles,
      itemStyle: barStyles,
    }))
  }, [chartData])

  const renderChart = useMemo(() => {
    const chartOptions = {
      series: renderDatasets,
      grid: { top: 24, right: 0, bottom: 64, left: 0 },
      xAxis: {
        type: 'category',
        data: chartData.categories.map(c => c),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: XaxisLabelStyles,
      },
      yAxis: {
        show: false,
        type: 'value',
        axisLabel: YaxisLabelStyles,
      },
      tooltip: {
        trigger: 'axis',
        ...tooltipStyles,
      },
      legend: legendStyles,
    }

    return (
      <AcBox
        width="100%"
        height={200}
        style={
          {
            '--ac-charts-font-family': theme.typography.fontFamily,
          } as React.CSSProperties
        }>
        <ReactEChartsCore
          style={{
            height: 200,
            width: '100%',
          }}
          echarts={echarts}
          option={chartOptions}
        />
      </AcBox>
    )
  }, [chartData])

  return renderChart
}
